import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, LoaderCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* Supervisor / admin only: a persistent banner on EVERY page while the face
   verification engine is not healthy. Staff never see engine state — their
   punches keep working (saved as Unverified, re-verified automatically) and
   it is the supervisors who must know to contact support. Polls the
   reviewer-accessible system-health endpoint once a minute. */
export const FaceEngineBanner = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const isReviewer = user?.role === "admin" || user?.role === "supervisor";
  const { data } = useQuery({
    queryKey: ["system-health", "banner"],
    queryFn: async () => (await api.get("/admin/system-health")).data,
    enabled: isReviewer,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 30_000,
  });
  if (!isReviewer || !data) return null;
  const status = data.faceService?.status;
  if (status === "ok") return null;
  const loading = status === "loading";
  const pending = data.faceService?.pendingVerifications || 0;
  const Icon = loading ? LoaderCircle : ShieldAlert;
  return (
    <div
      role="alert"
      data-testid="face-engine-banner"
      data-status={status}
      className={cn(
        "border-b px-4 py-2.5 sm:px-6",
        loading ? "border-amber-200 bg-amber-50 text-amber-900"
                : "border-rose-200 bg-rose-50 text-rose-950",
      )}
    >
      <div className="mx-auto flex max-w-6xl items-start gap-3">
        <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", loading && "animate-spin")} />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold" data-testid="face-engine-banner-title">
            {loading ? t("engineLoadingTitle") : t("engineDownTitle")}
            {pending > 0 && (
              <span className="ml-2 rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium"
                data-testid="face-engine-banner-pending">
                {pending} {t("engineAwaiting")}
              </span>
            )}
          </p>
          <p className="text-xs opacity-90">{loading ? t("engineLoadingBody") : t("engineDownBody")}</p>
        </div>
        <Link to="/admin/health" data-testid="face-engine-banner-health-link"
          className="hidden shrink-0 text-xs font-semibold underline-offset-2 hover:underline sm:inline">
          {t("engineViewHealth")}
        </Link>
      </div>
    </div>
  );
};
